import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { useSearchParams, useNavigate, useOutletContext } from 'react-router-dom';
import { Globe, AlertTriangle, PenLine, Plus, ArrowLeft, Map as MapIcon } from 'lucide-react';
import { movementStoriesApi } from '../../api/client';
import { useTranslation } from '../../i18n';
import MovementStoryCard from '../../components/MovementStoryCard';
import PageHeader from '../../components/PageHeader';
import PageGuide from '../../components/PageGuide';
import StoryCarousel from './StoryCarousel';
import MovementHero from './MovementHero';
import MovementSkeleton from './MovementSkeleton';
import StoryWizard from '../story/StoryWizard';
import MyStoriesPanel from '../story/MyStoriesPanel';
import './MovementFeed.css';

const MOVEMENT_CATEGORIES = ['salvation', 'healing', 'provision', 'persecution', 'revival', 'other'];
const MOVEMENT_REGIONS    = ['africa', 'asia', 'middle_east', 'europe', 'americas', 'oceania'];

// maplibre-gl is ~230KB gzipped plus the world outlines — none of which should
// reach a reader who never opens the Map tab.
const MovementMapPanel = lazy(() => import('./map/MovementMapPanel'));

// How many vertical cards between each interspersed carousel row.
const CARDS_PER_SECTION = 4;

// Deterministic daily shuffle (seed = today's date), mirrors CoursesScreen so the
// feed "changes up" day to day but stays stable as you scroll/navigate.
function shuffleWithSeed(arr) {
  const seed = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    h = (h * 1664525 + 1013904223) | 0;
    const j = ((h >>> 0) % (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const reactionTotal = (s) => Object.values(s.reactions || {}).reduce((a, b) => a + (b || 0), 0);

/**
 * The "Stories" screen. Two URL-driven modes:
 *  - Discover (no params): a shuffled vertical feed of every story (full cards)
 *    with themed carousels (most loved, latest, per-category, per-region)
 *    interspersed every few cards — modeled on the Growth Explore tab.
 *  - List (?category= or ?region=): the classic paginated full-card list, used
 *    by the carousels' "See all" links. Deep-linkable; no filter-chip UI.
 */
export default function MovementFeed() {
  // Rendered by HomeLayout/GrowLayout and placed here so the strip sits under
  // this screen's title, matching Prayer and People. Null when this screen is
  // reached outside its section layout.
  const { sectionTabs } = useOutletContext() || {};
  const { t } = useTranslation('movement');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // 'explore' is the default and replaces the old 'world' value. Nothing links
  // ?tab=world; ?tab=mine is linked from StoryView and ProfileScreen and still
  // works, as do bare /stories and the ?category= / ?region= carousel deep links.
  const rawTab   = params.get('tab');
  const tab      = rawTab === 'mine' ? 'mine' : rawTab === 'map' ? 'map' : 'explore';
  const category = params.get('category') || '';
  const region   = params.get('region')   || '';
  const placeKey = params.get('placeKey') || '';
  const listMode = tab === 'explore' && !!(category || region || placeKey);

  // Discover-mode state
  const [pool, setPool] = useState(null);
  const [discoverLoading, setDiscoverLoading] = useState(true);
  const [discoverError, setDiscoverError] = useState('');

  // List-mode state
  const [stories, setStories]         = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]         = useState(false);
  const [nextCursor, setNextCursor]   = useState(null);
  const [listError, setListError]     = useState('');

  const [showSubmit, setShowSubmit] = useState(false);

  const openStory = (id) => navigate(`/movement/${id}`);

  // ── Discover ────────────────────────────────────────────────────────────────
  const loadDiscover = useCallback(async () => {
    setDiscoverLoading(true); setDiscoverError('');
    try {
      const d = await movementStoriesApi.discover();
      setPool(d.stories || []);
    } catch (err) { setDiscoverError(err.message || t('failedLoad')); }
    finally { setDiscoverLoading(false); }
  }, [t]);

  useEffect(() => { if (tab === 'explore' && !listMode) loadDiscover(); }, [tab, listMode, loadDiscover]);

  async function reactDiscover(id, emoji) {
    try {
      const d = await movementStoriesApi.react(id, emoji);
      setPool(ss => (ss || []).map(s => s.id === id ? { ...s, reactions: d.reactions, myReaction: d.myReaction } : s));
    } catch {}
  }

  const featured = useMemo(() => (pool || []).filter(s => s.featured).slice(0, 2), [pool]);

  const shuffledFeed = useMemo(() => shuffleWithSeed(pool || []), [pool]);

  const carouselRows = useMemo(() => {
    const all = pool || [];
    if (all.length < 2) return [];
    const rows = [];

    const loved = [...all].filter(s => reactionTotal(s) > 0)
      .sort((a, b) => reactionTotal(b) - reactionTotal(a)).slice(0, 10);
    if (loved.length >= 2) rows.push({ key: 'most_loved', title: t('mostLoved'), stories: loved });

    const latest = [...all]
      .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)).slice(0, 10);
    if (latest.length >= 2) rows.push({ key: 'latest', title: t('latest'), stories: latest });

    MOVEMENT_CATEGORIES.forEach(cat => {
      const list = all.filter(s => s.category === cat).slice(0, 10);
      if (list.length >= 2) rows.push({ key: `cat_${cat}`, title: t(`categories.${cat}`), stories: list, onSeeAll: () => setParams({ category: cat }) });
    });

    MOVEMENT_REGIONS.forEach(code => {
      const list = all.filter(s => s.region === code).slice(0, 10);
      if (list.length >= 2) rows.push({ key: `region_${code}`, title: t(`byRegion.${code}`), stories: list, onSeeAll: () => setParams({ region: code }) });
    });

    return shuffleWithSeed(rows);
  }, [pool, t, setParams]);

  // ── List (See all) ────────────────────────────────────────────────────────────
  const listQuery = useCallback((before) => {
    const qs = new URLSearchParams();
    if (category) qs.set('category', category);
    if (region)   qs.set('region', region);
    if (placeKey) qs.set('placeKey', placeKey);
    if (before)   qs.set('before', before);
    return `?${qs.toString()}`;
  }, [category, region, placeKey]);

  const loadList = useCallback(async () => {
    setListLoading(true); setListError('');
    try {
      const d = await movementStoriesApi.list(listQuery());
      setStories(d.stories || []);
      setHasMore(!!d.hasMore);
      setNextCursor(d.nextCursor || null);
    } catch (err) { setListError(err.message || t('failedLoad')); }
    finally { setListLoading(false); }
  }, [listQuery, t]);

  useEffect(() => { if (listMode) loadList(); }, [listMode, loadList]);

  async function loadMore() {
    if (!hasMore || loadingMore || !nextCursor) return;
    setLoadingMore(true);
    try {
      const d = await movementStoriesApi.list(listQuery(nextCursor));
      setStories(prev => [...prev, ...(d.stories || [])]);
      setHasMore(!!d.hasMore);
      setNextCursor(d.nextCursor || null);
    } catch {}
    finally { setLoadingMore(false); }
  }

  async function reactList(id, emoji) {
    try {
      const d = await movementStoriesApi.react(id, emoji);
      setStories(ss => ss.map(s => s.id === id ? { ...s, reactions: d.reactions, myReaction: d.myReaction } : s));
    } catch {}
  }

  // Vertical feed of full cards with carousels interspersed (Growth-style).
  function renderDiscoveryFeed() {
    const els = [];
    let ci = 0;
    const pushCarousel = (row) => els.push(
      <div className="ms-feed-carousel" key={`carousel-${row.key}`}>
        <StoryCarousel title={row.title} stories={row.stories} onOpen={openStory} onSeeAll={row.onSeeAll} />
      </div>
    );
    for (let i = 0; i < shuffledFeed.length; i++) {
      if (i > 0 && i % CARDS_PER_SECTION === 0 && ci < carouselRows.length) { pushCarousel(carouselRows[ci]); ci++; }
      const s = shuffledFeed[i];
      els.push(<MovementStoryCard key={s.id} story={s} onReact={reactDiscover} />);
    }
    while (ci < carouselRows.length) { pushCarousel(carouselRows[ci]); ci++; }
    return els;
  }

  // In placeKey mode the label comes from the stories themselves — the key
  // ("KE::nairobi") is an identifier, not something to show a reader.
  const listLabel = category
    ? t(`categories.${category}`)
    : region
      ? t(`byRegion.${region}`)
      : placeKey
        ? (stories[0]?.location || t('map.thisPlace'))
        : '';

  return (
    <div className="page">
      {/* Stories is a tab of the Home section now, so its own Map / Explore /
          Mine strip is gone — that would have stacked two strips on the screen
          users land on first. Explore is the view; Map and My Stories are
          labelled buttons beside the title, and both still deep-link by
          ?tab= exactly as before. */}
      <PageHeader
        title={t('screenTitle')}
        subtitle={t('screenSubtitle')}
        action={
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <PageGuide screenKey="stories" />
          {tab === 'explore' ? (
            <div className="ms-view-switch">
              <button className="ms-view-btn" onClick={() => setParams({ tab: 'map' })}>
                <MapIcon size={15} strokeWidth={2} /> {t('tabMap')}
              </button>
              <button className="ms-view-btn" onClick={() => setParams({ tab: 'mine' })}>
                <PenLine size={15} strokeWidth={2} /> {t('tabMine')}
              </button>
            </div>
          ) : (
            <button className="ms-view-btn" onClick={() => setParams({})}>
              <ArrowLeft size={15} strokeWidth={2} /> {t('tabExplore')}
            </button>
          )}
          </div>
        } />
      {sectionTabs}

      {tab === 'mine' ? (
        <MyStoriesPanel />
      ) : tab === 'map' ? (
        <Suspense fallback={<MovementSkeleton />}>
          <MovementMapPanel />
        </Suspense>
      ) : (
        <>
      {listMode ? (
        /* ── List mode (See all) ── */
        <>
          <div className="ms-list-head">
            <button className="ms-list-back" onClick={() => setParams({})}>
              <ArrowLeft size={16} /> {t('allStories')}
            </button>
            {listLabel && <h2 className="ms-list-title">{listLabel}</h2>}
          </div>
          {listLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><span className="spinner" style={{ width: 32, height: 32 }} /></div>
          ) : listError ? (
            <div className="empty-state">
              <AlertTriangle size={36} strokeWidth={1.5} color="var(--text-lt)" />
              <h3>{t('somethingWrong')}</h3><p>{listError}</p>
              <button className="btn btn-primary" onClick={loadList}>{t('retry')}</button>
            </div>
          ) : stories.length === 0 ? (
            <div className="empty-state">
              <Globe size={40} strokeWidth={1.25} color="var(--orange)" />
              <h3>{t('emptyTitle')}</h3><p>{t('emptyBody')}</p>
            </div>
          ) : (
            <div className="feed-list">
              {stories.map(s => <MovementStoryCard key={s.id} story={s} onReact={reactList} />)}
              {hasMore && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 24px' }}>
                  <button className="btn btn-outline" onClick={loadMore} disabled={loadingMore}>
                    {loadingMore
                      ? <><span className="spinner" style={{ width: 14, height: 14 }} /> {t('loadingMore')}</>
                      : t('loadMore')}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : discoverLoading ? (
        <MovementSkeleton />
      ) : discoverError ? (
        <div className="empty-state">
          <AlertTriangle size={36} strokeWidth={1.5} color="var(--text-lt)" />
          <h3>{t('somethingWrong')}</h3><p>{discoverError}</p>
          <button className="btn btn-primary" onClick={loadDiscover}>{t('retry')}</button>
        </div>
      ) : !pool || pool.length === 0 ? (
        <div className="empty-state">
          <Globe size={40} strokeWidth={1.25} color="var(--orange)" />
          <h3>{t('emptyTitle')}</h3>
          <p>{t('emptyBody')}</p>
          <button className="btn btn-primary" onClick={() => setShowSubmit(true)}>
            <PenLine size={15} /> {t('submit')}
          </button>
        </div>
      ) : (
        /* ── Discover mode ── */
        <>
          {featured.length > 0 && <MovementHero stories={featured} onOpen={openStory} />}
          <div className="ms-feed">{renderDiscoveryFeed()}</div>
        </>
      )}

          <button className="fab" onClick={() => setShowSubmit(true)} aria-label={t('submit')}>
            <Plus size={22} strokeWidth={2} />
          </button>

          {/* Authoring goes through the single Story wizard. Choosing "public" in its
              final step auto-submits the story to the Movement feed (pending review). */}
          {showSubmit && (
            <StoryWizard
              onClose={() => setShowSubmit(false)}
              onSaved={() => { setShowSubmit(false); listMode ? loadList() : loadDiscover(); }}
            />
          )}
        </>
      )}
    </div>
  );
}
